(function () {
  const viewTransitionRenderBlocker = document.getElementById('view-transition-render-blocker');
  const userAgent = navigator.userAgent || '';
  const isInAppBrowser =
    window.RUDEDOG_DISABLE_VIEW_TRANSITIONS ||
    /(FBAN|FBAV|FB_IAB|Instagram|Line\/|;\s*wv\)|\bwv\b)/i.test(userAgent);

  if (isInAppBrowser) {
    viewTransitionRenderBlocker?.remove();
    document.documentElement.classList.add('rd-in-app-browser');
    return;
  }

  // Remove the view transition render blocker if the user has reduced motion enabled or is on a low power device.
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || isLowPowerDevice()) {
    viewTransitionRenderBlocker?.remove();
  } else {
    // If the browser didn't manage to parse the main content quickly, at least let the user see something.
    // We're aiming for the FCP to be under 1.8 seconds since the navigation started.
    const RENDER_BLOCKER_TIMEOUT_MS = Math.max(0, 1800 - performance.now());

    setTimeout(() => {
      viewTransitionRenderBlocker?.remove();
    }, RENDER_BLOCKER_TIMEOUT_MS);
  }

  const idleCallback = typeof requestIdleCallback === 'function' ? requestIdleCallback : setTimeout;

  window.addEventListener('pageswap', async (event) => {
    const { viewTransition } = /** @type {PageSwapEvent} */ (event);
    consumeExpectedTransitionRejection(viewTransition);

    if (shouldSkipViewTransition(viewTransition)) {
      safeSkipTransition(/** @type {ViewTransition | null} */ (viewTransition));
      return;
    }

    // Cancel view transition on user interaction to improve INP (Interaction to Next Paint)
    ['pointerdown', 'keydown'].forEach((eventName) => {
      document.addEventListener(
        eventName,
        () => {
          safeSkipTransition(viewTransition);
        },
        { once: true }
      );
    });

    // Clean in case you landed on the pdp first. We want to remove the default transition type on the PDP media gallery so there is no duplicate transition name
    document
      .querySelectorAll('[data-view-transition-type]:not([data-view-transition-triggered])')
      .forEach((element) => {
        element.removeAttribute('data-view-transition-type');
      });

    const transitionTriggered = document.querySelector('[data-view-transition-triggered]');
    const transitionType = transitionTriggered?.getAttribute('data-view-transition-type');

    if (transitionType) {
      safeSetTransitionType(viewTransition, transitionType);
      sessionStorage.setItem('custom-transition-type', transitionType);
    } else {
      safeSetTransitionType(viewTransition, 'page-navigation');
      sessionStorage.removeItem('custom-transition-type');
    }
  });

  window.addEventListener('pagereveal', async (event) => {
    const { viewTransition } = /** @type {PageRevealEvent} */ (event);
    consumeExpectedTransitionRejection(viewTransition);

    if (shouldSkipViewTransition(viewTransition)) {
      safeSkipTransition(/** @type {ViewTransition | null} */ (viewTransition));
      return;
    }

    const customTransitionType = sessionStorage.getItem('custom-transition-type');

    if (customTransitionType) {
      safeSetTransitionType(viewTransition, customTransitionType);

      try {
        await viewTransition.finished;
      } catch (_error) {
        // A skipped navigation transition can reject after the page has already rendered.
      }

      safeSetTransitionType(viewTransition, 'page-navigation');

      idleCallback(() => {
        sessionStorage.removeItem('custom-transition-type');
        document.querySelectorAll('[data-view-transition-type]').forEach((element) => {
          element.removeAttribute('data-view-transition-type');
        });
      });
    } else {
      safeSetTransitionType(viewTransition, 'page-navigation');
    }
  });

  /**
   * @param {ViewTransition | null} viewTransition
   * @returns {viewTransition is null}
   */
  function shouldSkipViewTransition(viewTransition) {
    return !(viewTransition instanceof ViewTransition) || isLowPowerDevice();
  }

  /**
   * Android WebView can abort a cross-document transition during navigation.
   * The page is already usable, so consume the expected rejected promise.
   * @param {ViewTransition | null} viewTransition
   */
  function consumeExpectedTransitionRejection(viewTransition) {
    try {
      viewTransition?.finished?.catch(() => {});
    } catch (_error) {
      // Some partial View Transition implementations do not expose `finished`.
    }
  }

  /**
   * @param {ViewTransition | null} viewTransition
   */
  function safeSkipTransition(viewTransition) {
    try {
      consumeExpectedTransitionRejection(viewTransition);
      viewTransition?.skipTransition();
    } catch (_error) {
      // The transition may already be finished or aborted.
    }
  }

  /**
   * @param {ViewTransition} viewTransition
   * @param {string} type
   */
  function safeSetTransitionType(viewTransition, type) {
    try {
      viewTransition.types.clear();
      viewTransition.types.add(type);
    } catch (_error) {
      // The transition can be aborted between navigation events.
    }
  }

  /*
   * We can't import this logic from utilities.js here, but we should keep them in sync.
   */
  function isLowPowerDevice() {
    /* Skip ESLint compatibility check. Number(undefined) <= 2 is always false anyway. */
    /* eslint-disable-next-line compat/compat */
    return Number(navigator.hardwareConcurrency) <= 2 || Number(navigator.deviceMemory) <= 2;
  }
})();
